import React, { useState } from 'react';
import { CheckCircle2Icon, PhoneIcon, MessageCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
type Status = 'idle' | 'submitting' | 'success';
const fieldClass = 'mt-1.5 w-full rounded-lg border border-navy/15 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 transition-colors duration-150 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-navy';
export function BookingForm() {
  const {
    t
  } = useLanguage();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    vehicle: '',
    date: '',
    time: '',
    message: '',
    callback: false
  });
  const update = (key: keyof typeof form, value: string | boolean) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError(t.booking.errorRequired);
      return;
    }
    setError(null);
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 700);
  };
  const reset = () => {
    setForm({
      name: '',
      phone: '',
      email: '',
      service: '',
      vehicle: '',
      date: '',
      time: '',
      message: '',
      callback: false
    });
    setStatus('idle');
  };
  return <section id="contact" className="bg-navy py-20 sm:py-24" data-id="element-49" data-mp="189s06p2ze4vj">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:px-8" data-id="element-50" data-mp="13bqjgat12hn8">
        <div data-id="element-51" data-mp="20rnwbcivykfb">
          <p className="text-sm font-semibold text-brand-cyan" data-id="element-52" data-mp="20s5zyu7vo0x9">{t.booking.kicker}</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-id="element-53" data-mp="1vzw12oosbqiu">
            {t.booking.title}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-white/70" data-id="element-54" data-mp="3q91i6iww24lt">{t.booking.subtitle}</p>

          <p className="mt-10 text-sm font-medium text-white/60" data-id="element-55" data-mp="3v6jixm0r5diq">{t.booking.orCall}</p>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-56" data-mp="3ghnahnus9yhl">
            <a href={business.phoneHref} className="inline-flex items-center gap-3 rounded-lg border border-white/20 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-brand-cyan hover:text-brand-cyan" data-id="element-57" data-mp="3lgrl8ixcw4a1">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-58" data-mp="2xjucmzz0oer2" />
              {business.phoneDisplay}
            </a>
            <a href={business.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 rounded-lg border border-white/20 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-brand-cyan hover:text-brand-cyan" data-id="element-59" data-mp="32hvvpkwg0e2j">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-60" data-mp="2l78pxsra7vok" />
              WhatsApp · {business.phoneDisplay}
            </a>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lift sm:p-8" data-id="element-61" data-mp="2uzxzgi39uzh9">
          {status === 'success' ? <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center" data-id="element-62" data-mp="2q6v2s13779wu">
              <CheckCircle2Icon className="h-12 w-12 text-brand-blue" aria-hidden="true" data-id="element-63" data-mp="33y9o4ekkvn72" />
              <h3 className="mt-4 font-display text-2xl font-bold text-navy" data-id="element-64" data-mp="2z083hp7i7fth">
                {t.booking.successTitle}
              </h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink/70" data-id="element-65" data-mp="38yxmxmf1t8bu">
                {t.booking.successBody}
              </p>
              <button type="button" onClick={reset} className="mt-6 rounded-lg border border-navy/15 px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-150 hover:border-brand-blue hover:text-brand-blue" data-id="element-66" data-mp="1jj7q4q3jxrjf">
                {t.booking.successReset}
              </button>
            </div> : <form onSubmit={onSubmit} noValidate data-id="element-67" data-mp="flm4kjnst990">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-68" data-mp="fmx427a7nuub">
                <div data-id="element-69" data-mp="jo0mn1jn84z8">
                  <label className={labelClass} htmlFor="name" data-id="element-70" data-mp="jn7b7n9qorar">
                    {t.booking.fields.name} *
                  </label>
                  <input id="name" required value={form.name} onChange={e => update('name', e.target.value)} placeholder={t.booking.placeholders.name} className={fieldClass} data-id="element-71" data-mp="3j11ab8p58woc" />
                </div>
                <div data-id="element-72" data-mp="30d61824h871k">
                  <label className={labelClass} htmlFor="phone" data-id="element-73" data-mp="30bl4q6ylqe12">
                    {t.booking.fields.phone} *
                  </label>
                  <input id="phone" type="tel" required value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t.booking.placeholders.phone} className={fieldClass} data-id="element-74" data-mp="34blom74gzhfi" />
                </div>
                <div data-id="element-75" data-mp="2iueb01bcx2oo">
                  <label className={labelClass} htmlFor="email" data-id="element-76" data-mp="2ix0g7uarz63q">
                    {t.booking.fields.email}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-77" data-mp="2954k2os91q25">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder={t.booking.placeholders.email} className={fieldClass} data-id="element-78" data-mp="243f54y1wos8s" />
                </div>
                <div data-id="element-79" data-mp="1gi4qlb1e5i6d">
                  <label className={labelClass} htmlFor="service" data-id="element-80" data-mp="1ggtemnxsl446">
                    {t.booking.fields.service}
                  </label>
                  <select id="service" value={form.service} onChange={e => update('service', e.target.value)} className={fieldClass} data-id="element-81" data-mp="16k8981hv1qzt">
                    <option value="" data-id="element-82" data-mp="1z9xamfyhft1y">{t.booking.selectService}</option>
                    {t.booking.serviceOptions.map(option => <option key={option} value={option} data-id="element-83" data-mp="2477bqsv8ht5m">
                        {option}
                      </option>)}
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-84" data-mp="1ugclzeis4bff">
                  <label className={labelClass} htmlFor="vehicle" data-id="element-85" data-mp="2i789hzg7ckgv">
                    {t.booking.fields.vehicle}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-86" data-mp="2mzi6fzfqiksn">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <input id="vehicle" value={form.vehicle} onChange={e => update('vehicle', e.target.value)} placeholder={t.booking.placeholders.vehicle} className={fieldClass} data-id="element-87" data-mp="6kwmfyhc7nfc" />
                </div>
                <div data-id="element-88" data-mp="3or2mc27qym0y">
                  <label className={labelClass} htmlFor="date" data-id="element-89" data-mp="3oqjcl8s933y0">
                    {t.booking.fields.date}
                  </label>
                  <input id="date" type="date" value={form.date} onChange={e => update('date', e.target.value)} className={fieldClass} data-id="element-90" data-mp="1lbn3my99d23" />
                </div>
                <div data-id="element-91" data-mp="1d2jrw9ctcv6s">
                  <label className={labelClass} htmlFor="time" data-id="element-92" data-mp="18yvirm8j9rjf">
                    {t.booking.fields.time}
                  </label>
                  <input id="time" type="time" value={form.time} onChange={e => update('time', e.target.value)} className={fieldClass} data-id="element-93" data-mp="32infapg1x564" />
                </div>
                <div className="sm:col-span-2" data-id="element-94" data-mp="3h68kqq0efgh7">
                  <label className={labelClass} htmlFor="message" data-id="element-95" data-mp="3h81sk8mr06qs">
                    {t.booking.fields.message}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-96" data-mp="3lag8okz18sed">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <textarea id="message" rows={3} value={form.message} onChange={e => update('message', e.target.value)} placeholder={t.booking.placeholders.message} className={fieldClass} data-id="element-97" data-mp="3v71eh8ivm7j9" />
                </div>
              </div>

              <label className="mt-5 flex items-center gap-3 text-sm text-ink/75" data-id="element-98" data-mp="mx8va8h2cc84">
                <input type="checkbox" checked={form.callback} onChange={e => update('callback', e.target.checked)} className="h-4 w-4 rounded border-navy/25 text-brand-orange focus:ring-brand-orange" data-id="element-99" data-mp="i4z1ghctus0k" />
                {t.booking.callback}
              </label>

              {error && <p className="mt-4 flex items-center gap-2 rounded-lg bg-brand-orange/10 px-3 py-2.5 text-sm font-medium text-brand-orangeDark" data-id="element-100" data-mp="11thqmyxu25f9">
                  <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-101" data-mp="wt3h1cgl2nf4" />
                  {error}
                </p>}

              <button type="submit" disabled={status === 'submitting'} className="mt-6 w-full rounded-lg bg-brand-orange px-6 py-4 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-orangeDark disabled:opacity-70" data-id="element-102" data-mp="rxo6atdvh7mx">
                {status === 'submitting' ? t.booking.submitting : t.booking.submit}
              </button>
            </form>}
        </div>
      </div>
    </section>;
}